import React, { useState } from "react";
import { getName } from "country-list";
import { Link, useParams } from "react-router-dom";
import {
  useClubs,
  useCourts,
  useCreateCourt,
  useDeleteCourt,
  useUpdateCourt,
} from "../apiCalls/clubApiCalls";
import {
  Badge,
  Button,
  Card,
  Cell,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Loader,
  PageHeader,
  Row,
  Select,
  Table,
} from "../components/ui";
import ConfirmDelete from "../components/ConfirmDelete";

const HEAD = [
  { label: "Court" },
  { label: "Surface" },
  { label: "Cover" },
  { label: "In use" },
  { label: "", key: "actions", align: "right" },
];

// Adding courts is the most repeated action on this page, so the form sits
// inline and keeps focus instead of opening a dialog each time.
function AddCourtForm({ clubId }) {
  const [name, setName] = useState("");
  const [surface, setSurface] = useState("panoramic");
  const [isIndoor, setIsIndoor] = useState("false");
  const { mutate: create, isLoading } = useCreateCourt();

  const handleSubmit = (event) => {
    event.preventDefault();
    create(
      { club: clubId, name, surface, isIndoor: isIndoor === "true" },
      { onSuccess: () => setName("") }
    );
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-wrap items-end gap-3 border-t border-sand bg-paper p-4"
    >
      <div className="w-full sm:w-48">
        <Field label="Court name">
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Court 1"
            required
          />
        </Field>
      </div>
      <div className="w-full sm:w-40">
        <Field label="Surface">
          <Select
            value={surface}
            onChange={(event) => setSurface(event.target.value)}
          >
            <option value="panoramic">Panoramic</option>
            <option value="standard">Standard</option>
          </Select>
        </Field>
      </div>
      <div className="w-full sm:w-36">
        <Field label="Cover">
          <Select
            value={isIndoor}
            onChange={(event) => setIsIndoor(event.target.value)}
          >
            <option value="false">Outdoor</option>
            <option value="true">Indoor</option>
          </Select>
        </Field>
      </div>
      <Button type="submit" variant="primary" disabled={isLoading || !name}>
        {isLoading ? "Adding" : "Add court"}
      </Button>
    </form>
  );
}

export default function ClubDetail() {
  const { clubId } = useParams();
  const [deleting, setDeleting] = useState(null);

  const clubs = useClubs();
  const courts = useCourts(clubId);
  const { mutate: update } = useUpdateCourt();
  const { mutate: remove, isLoading: isDeleting } = useDeleteCourt();

  const club = (clubs.data || []).find((item) => item._id === clubId);

  if (clubs.isLoading) return <Loader label="Loading club" />;
  if (clubs.isError)
    return <ErrorState message="Couldn't load clubs." onRetry={clubs.refetch} />;
  if (!club)
    return (
      <>
        <PageHeader title="Club not found" />
        <p className="text-sm text-ink-soft">
          That club no longer exists.{" "}
          <Link to="/clubs" className="underline">
            Back to clubs
          </Link>
          .
        </p>
      </>
    );

  return (
    <>
      <PageHeader
        title={club.name}
        subtitle={`${[club.city, getName(club.country) || club.country].filter(Boolean).join(", ")}${club.address ? ` · ${club.address}` : ""}`}
        actions={
          <Link to="/clubs">
            <Button>Back to clubs</Button>
          </Link>
        }
      />

      <Card>
        {courts.isLoading ? (
          <Loader label="Loading courts" />
        ) : courts.isError ? (
          <ErrorState
            message="Couldn't load courts."
            onRetry={courts.refetch}
          />
        ) : (courts.data || []).length === 0 ? (
          <EmptyState message="No courts here yet. Add one below." />
        ) : (
          <Table head={HEAD}>
            {courts.data.map((court) => (
              <Row key={court._id}>
                <Cell className="font-medium">{court.name}</Cell>
                <Cell className="capitalize">{court.surface}</Cell>
                <Cell>{court.isIndoor ? "Indoor" : "Outdoor"}</Cell>
                <Cell>
                  <Badge tone={court.isActive ? "active" : "neutral"}>
                    {court.isActive ? "In use" : "Out of use"}
                  </Badge>
                </Cell>
                <Cell align="right">
                  <div className="flex justify-end gap-2">
                    {/* Taking a court out of use is the everyday action —
                        resurfacing, maintenance — so it sits next to delete
                        rather than behind an edit dialog. */}
                    <Button
                      className="px-3 py-1.5 text-xs"
                      onClick={() =>
                        update({ id: court._id, isActive: !court.isActive })
                      }
                    >
                      {court.isActive ? "Take out of use" : "Put back in use"}
                    </Button>
                    <Button
                      variant="danger"
                      className="px-3 py-1.5 text-xs"
                      onClick={() => setDeleting(court)}
                    >
                      Delete
                    </Button>
                  </div>
                </Cell>
              </Row>
            ))}
          </Table>
        )}

        <AddCourtForm clubId={clubId} />
      </Card>

      {deleting && (
        <ConfirmDelete
          title={`Delete ${deleting.name}?`}
          body="The court is removed permanently. If it's only temporarily unavailable, take it out of use instead."
          isDeleting={isDeleting}
          onConfirm={() =>
            remove(deleting._id, { onSuccess: () => setDeleting(null) })
          }
          onClose={() => setDeleting(null)}
        />
      )}
    </>
  );
}
